import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SavedChartType = "bar" | "line" | "pie" | "scatter" | "area" | "kpi";

export interface SavedChart {
  id: string;
  title: string;
  chart_type: SavedChartType;
  x_field: string | null;
  y_field: string | null;
  aggregation: string;
  data: any[];
  config: Record<string, any>;
  origin: string;
  position: number;
  source_type: string | null;
  source_name: string | null;
  source_id: string | null;
  created_at: string;
}

export interface SaveChartInput {
  title: string;
  chartType: SavedChartType;
  xField?: string | null;
  yField?: string | null;
  aggregation?: string;
  data: any[];
  config?: Record<string, any>;
  origin?: "generator" | "template" | "widget";
  sourceType?: string | null;
  sourceName?: string | null;
  sourceId?: string | null;
}

/** Persistence for dashboard charts and KPI widgets (`saved_charts`). */
export const useSavedCharts = () => {
  const queryClient = useQueryClient();

  const { data: charts = [], isLoading } = useQuery({
    queryKey: ["saved-charts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("saved_charts")
        .select("*")
        .eq("user_id", user.id)
        .order("position", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SavedChart[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["saved-charts"] });

  const saveChart = useMutation({
    mutationFn: async (input: SaveChartInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in to save charts.");
      if (!input.data?.length) throw new Error("There is no data to save on this chart.");

      const { data, error } = await supabase
        .from("saved_charts")
        .insert({
          user_id: user.id,
          title: input.title,
          chart_type: input.chartType,
          x_field: input.xField ?? null,
          y_field: input.yField ?? null,
          aggregation: input.aggregation ?? "none",
          data: input.data.slice(0, 500) as any,
          config: (input.config ?? {}) as any,
          origin: input.origin ?? "generator",
          position: charts.length,
          source_type: input.sourceType ?? null,
          source_name: input.sourceName ?? null,
          source_id: input.sourceId ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as SavedChart;
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || "Could not save the chart"),
  });

  const deleteChart = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in.");
      const { error } = await supabase
        .from("saved_charts")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e?.message || "Could not remove the chart"),
  });

  const moveChart = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: -1 | 1 }) => {
      const index = charts.findIndex((c) => c.id === id);
      const swapWith = index + direction;
      if (index < 0 || swapWith < 0 || swapWith >= charts.length) return;
      const a = charts[index];
      const b = charts[swapWith];
      const [{ error: e1 }, { error: e2 }] = await Promise.all([
        supabase.from("saved_charts").update({ position: swapWith }).eq("id", a.id),
        supabase.from("saved_charts").update({ position: index }).eq("id", b.id),
      ]);
      if (e1 || e2) throw e1 ?? e2;
    },
    onSuccess: invalidate,
  });

  return { charts, isLoading, saveChart, deleteChart, moveChart, refresh: invalidate };
};
